import React, { useState, useEffect } from "react";
import { useListExpenses } from "./api";
import DataTable from "@components/DataTable";
import {
  useGetAccountQuery,
  useListLineOfBusinessesForAccountQuery,
} from "@generated";
import CurrencyFormat from "react-currency-format";
import { RootState } from "@reducers/rootReducer";
import { useSelector } from "react-redux";
import { Expense } from "../types/Expense";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil } from "@fortawesome/pro-solid-svg-icons";
import { ColumnDef } from "@tanstack/react-table";
import { Card, Row, Col, Button } from "react-bootstrap";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import "./styles/style.css";

const Show = ({ project_id }): JSX.Element => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const [selectedExpenseIds, setSelectedExpenseIds] = useState<string[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const {
    data: account,
    isLoading: accountLoading,
    isError: accountErr,
  } = useGetAccountQuery({ slug: accountSlug });

  const {
    data: lobs,
    isLoading: lobsLoading,
    isError: lobsErr,
  } = useListLineOfBusinessesForAccountQuery({ slug: accountSlug });

  const { expenses, expensesError, expensesLoading, pageMeta, refetch } =
    useListExpenses({
      page: {
        number: pageNumber,
        size: pageSize,
      },
      include: [
        "resource",
        "project-phase",
        "project-resource",
        "project",
        "expense-category",
      ],
      lobs: lobs && lobs.data && !lobsLoading ? lobs.data : [],
      filter: { active: true, project: project_id },
    });

  const formatCurrency = (price) => (
    <CurrencyFormat
      displayType="text"
      prefix={
        account?.data?.attributes?.["field-labels"]["currency_unit"]
          ? account.data.attributes["field-labels"]["currency_unit"]
          : "$"
      }
      isNumericString={true}
      thousandSeparator={true}
      value={(Number(price) || 0).toFixed(2)}
    />
  );

  const expenseColumns: ColumnDef<Expense>[] = [
    {
      header: "Expense",
      cell(props) {
        return (
          <div>
            {props.row.original.description}{" "}
            {props.row.original.id !== 0 && (
              <FontAwesomeIcon
                icon={faPencil}
                onClick={() => {
                  const expenseSelected = expenses.find(
                    (product) =>
                      Number(product.id) === Number(props.row.original.id)
                  );
                  // navigate("custom", {
                  //   state: expenseSelected,
                  // });
                }}
              />
            )}
          </div>
        );
      },
    },
    {
      header: "Resource",
      cell(props) {
        return props.row.original.resource;
      },
    },
    {
      header: "Phase",
      cell(props) {
        return props.row.original.phase;
      },
    },
    {
      header: "Quantity",
      cell(props) {
        return props.row.original.quantity;
      },
    },
    {
      header: "Unit Cost",
      cell(props) {
        return formatCurrency(props.row.original.unitCost);
      },
    },
    {
      header: "Total Cost",
      cell(props) {
        return formatCurrency(props.row.original.totalCost);
      },
    },
  ];

  return (
    <div className="expensePage">
      <Card>
        <Card.Header
          style={{
            backgroundColor: "#fff",
            color: "#1C2655",
            padding: "0px 16px",
          }}
        >
          <Row style={{ padding: "20px 0 0 20px", alignItems: "center" }}>
            <Col className="text24" sm={9}>
              Travel and Expenses
            </Col>
            <Col>
              <Button
                style={{ marginLeft: "60px" }}
                className="squareGreenButton"
              >
                Add Travel / Expense
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <hr style={{ marginLeft: "30px", marginRight: "30px" }} />
        <Card.Body style={{ marginTop: "-10px" }}>
          {!expenses || expensesLoading || !lobs || lobsLoading ? (
            <SmallSpinner />
          ) : (
            <DataTable
              classForTable="expenseTable"
              data={
                expenses?.length > 0
                  ? expenses
                  : [
                      {
                        id: 0,
                        description: "Add expenses to get started",
                      },
                    ]
              }
              columns={expenseColumns}
              selectable={expenses.length > 0}
              striped
              hover
              selectedIds={selectedExpenseIds}
              setSelectedIds={setSelectedExpenseIds}
              totalPages={pageMeta.pageCount}
              totalRows={pageMeta.recordCount}
              currentPage={pageNumber}
              setCurrentPage={setPageNumber}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              paginationEnabled
              pageSizeEnabled
            />
          )}
          {expenses.length > 0 && (
            <div style={{ marginLeft: "16px" }} className="text20">
              <strong>Subtotal:</strong>&nbsp;
              {formatCurrency(
                expenses
                  .map((e) => Number(e.totalCost || 0))
                  .reduce((acc, current) => acc + current, 0)
              )}
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default Show;
